// src/components/quickMessage/ComposeSheet.tsx -- bottom-sheet modal for the
// sender flow only (opened from a contact card's Quick msg button).
//
// Reply lives inside the expanded Banner instead -- this sheet never reaches
// the recipient-side flow. The recipient row is read-only (gotcha #14) and
// arrives via props: the contact card already chose them.
//
// Auto-focus: yes for compose (user clearly intends to type). The mobile
// keyboard-pop concern in gotcha #13 applies only to banner expand, where
// the user might have just been opening to read.

import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../api";
import { C, F } from "../../theme";
import { sendQuickMessage } from "../../lib/quickMessages";

export interface ComposeSheetProps {
  open: boolean;
  recipient: {
    hubUserId: number;
    firstName: string;
    lastName: string;
  };
  onCancel: () => void;
  /** Fired on a 201 Created. Caller is expected to dismiss the sheet and toast. */
  onSent: (info: { firstName: string }) => void;
}

const SHEET_KEYFRAMES = `
@keyframes qm-sheet-slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes qm-sheet-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
`;

export function ComposeSheet({
  open,
  recipient,
  onCancel,
  onSent,
}: ComposeSheetProps) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset state every time the sheet opens (new recipient may differ from
  // the last; stale draft would surprise the user).
  useEffect(() => {
    if (!open) return;
    setBody("");
    setError(null);
    setSubmitting(false);
    // Auto-focus on open. RequestAnimationFrame avoids the focus landing
    // before the slide-up animation paints.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Esc to cancel, unless we're mid-submit.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const trimmed = body.trim();
  const tooLong = trimmed.length > 100;
  const sendDisabled = submitting || trimmed.length === 0 || tooLong;

  async function handleSend() {
    if (sendDisabled) return;
    setSubmitting(true);
    setError(null);
    try {
      await sendQuickMessage({
        recipientId: recipient.hubUserId,
        body: trimmed,
      });
      onSent({ firstName: recipient.firstName });
    } catch (e) {
      // Keep the sheet open with the text intact; show inline error.
      setError(
        e instanceof ApiError ? e.message : "Couldn’t send. Try again.",
      );
      setSubmitting(false);
    }
  }

  const recipientFullName = `${recipient.firstName} ${recipient.lastName}`.trim();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Quick message"
      style={backdropStyle}
      onClick={() => {
        // Backdrop tap dismisses, except while a send is in flight (don't
        // discard the user's draft mid-submit).
        if (!submitting) onCancel();
      }}
    >
      <div
        style={sheetStyle}
        onClick={(e) => {
          // Taps inside the sheet must not bubble to the backdrop handler.
          e.stopPropagation();
        }}
      >
        <div style={grabberStyle} aria-hidden="true" />
        <div style={headerRowStyle}>
          <div style={eyebrowStyle}>Quick message</div>
          <div style={maxLabelStyle}>100 char max</div>
        </div>

        <div style={recipientRowStyle}>
          <div style={recipientAvatarStyle}>
            {(recipient.firstName[0] ?? "") + (recipient.lastName[0] ?? "")}
          </div>
          <div>
            <div style={recipientLabelStyle}>To</div>
            <div style={recipientNameStyle}>{recipientFullName}</div>
          </div>
        </div>

        <textarea
          ref={inputRef}
          value={body}
          maxLength={100}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a quick message…"
          disabled={submitting}
          style={textareaStyle}
          aria-label="Message body"
        />

        <div style={counterRowStyle}>
          <span style={helperTextStyle}>Replies appear on your home page.</span>
          <span aria-live="polite">
            <span style={{ color: body.length >= 90 ? C.brassDk : C.inkMute }}>
              {body.length}
            </span>{" "}
            / 100
          </span>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <div style={actionsRowStyle}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={cancelBtnStyle(submitting)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sendDisabled}
            style={sendBtnStyle(sendDisabled)}
          >
            <PlaneIcon /> Send quick message
          </button>
        </div>
      </div>
      <style>{SHEET_KEYFRAMES}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-icons
// ---------------------------------------------------------------------------

function PlaneIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const backdropStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(43, 32, 20, 0.32)",
  backdropFilter: "blur(2px)",
  WebkitBackdropFilter: "blur(2px)",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  zIndex: 200,
  animation: "qm-sheet-fade-in 0.2s ease-out",
};

const sheetStyle: CSSProperties = {
  width: "100%",
  maxWidth: 720,
  background: C.paper,
  borderRadius: "22px 22px 0 0",
  padding: "18px 18px calc(20px + env(safe-area-inset-bottom))",
  boxShadow: "0 -16px 40px rgba(122,84,11,0.18)",
  animation: "qm-sheet-slide-up 0.22s ease-out",
};

const grabberStyle: CSSProperties = {
  width: 38,
  height: 4,
  borderRadius: 2,
  background: C.hairline,
  margin: "-6px auto 14px",
};

const headerRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 6,
};

const eyebrowStyle: CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 12,
  fontWeight: 600,
  color: C.brassDk,
  letterSpacing: 0.2,
};

const maxLabelStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 700,
  color: C.inkMute,
  letterSpacing: 1,
  textTransform: "uppercase",
};

const recipientRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "8px 0 14px",
};

const recipientAvatarStyle: CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 14,
  background: `linear-gradient(135deg, ${C.brassLt}, ${C.brass})`,
  color: "#FFFCEC",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: F.serif,
  fontWeight: 700,
  fontSize: 16,
  flexShrink: 0,
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(122,84,11,0.18)",
};

const recipientLabelStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 13,
  fontStyle: "italic",
  color: C.inkMute,
};

const recipientNameStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 17,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
};

const textareaStyle: CSSProperties = {
  width: "100%",
  minHeight: 96,
  padding: "14px",
  background: C.card,
  border: `1px solid ${C.hairline}`,
  borderRadius: 14,
  fontFamily: F.body,
  fontSize: 15.5,
  color: C.ink,
  fontWeight: 500,
  lineHeight: 1.5,
  resize: "none",
  outline: "none",
  boxSizing: "border-box",
};

const counterRowStyle: CSSProperties = {
  marginTop: 6,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontFamily: F.mono,
  fontSize: 11,
  fontWeight: 700,
  color: C.inkMute,
  letterSpacing: 0.6,
};

const helperTextStyle: CSSProperties = {
  fontStyle: "italic",
  fontFamily: F.serif,
  fontSize: 12,
  fontWeight: 600,
  color: C.inkMute,
  letterSpacing: 0,
  textTransform: "none",
};

const errorStyle: CSSProperties = {
  marginTop: 10,
  padding: "10px 12px",
  background: C.signalBg,
  border: "1px solid rgba(184,58,43,0.22)",
  borderRadius: 10,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  color: C.signalDk,
};

const actionsRowStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  marginTop: 14,
};

function cancelBtnStyle(disabled: boolean): CSSProperties {
  return {
    padding: "11px 16px",
    borderRadius: 12,
    fontFamily: F.body,
    fontWeight: 800,
    fontSize: 14.5,
    background: "transparent",
    color: C.inkSoft,
    border: `1px solid ${C.hairline}`,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    minHeight: 44,
    letterSpacing: 0.2,
  };
}

function sendBtnStyle(disabled: boolean): CSSProperties {
  return {
    flex: 1,
    padding: "11px 16px",
    borderRadius: 12,
    fontFamily: F.body,
    fontWeight: 800,
    fontSize: 14.5,
    background: C.brassDk,
    color: C.brassGlow,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    boxShadow:
      "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.15)",
    minHeight: 44,
    letterSpacing: 0.2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  };
}
